import { connectSignal, createRoom, type SignalClient } from './signal';
import { createPeer, type Peer } from './peer';
import {
  buildShareLink,
  grabEls,
  readJoinCodeFromHash,
  showGame,
  showHosting,
  showJoining,
  showLobbyChoice,
  type LobbyEls,
} from './ui';
import {
  defaultPhysicsConfig,
  newGame,
  resetMatch,
  setGuestTarget,
  setHostTarget,
  step,
  type GameState,
} from './game/physics';
import { applyModifiers, MODIFIERS } from './game/modifiers';
import { audioApplyEvents, getVolume, initAudio, isMuted, setMuted, setVolume } from './game/audio';
import { applyCssPalette, pickRandomPalette } from './palettes';
import { applyRenderPalette } from './game/render';
import { applyRenderPaletteGL } from './game/render-gl';
import { setLobbyBgPalette } from './lobby-bg';
import { encodeInput, encodeState, parse, serialize } from './game/net';
import { applyEvents, fitCanvas, render, warmStaticCache, usingWebGL } from './game/render-router';
import { attachInput, type InputState } from './game/input';
import { tickBot } from './game/bot';
import { createHud, type Hud } from './game/hud';
import { applyHoverTransform } from './game/hover';
import { createStats, type Stats } from './game/stats';
import { createPerfCounter } from './perf';
import { startLobbyBg, stopLobbyBg } from './lobby-bg';
import { RINK, type PhysicsEvent, type Role } from './types';

const els = grabEls();

// Pick + apply a color palette for this session. Rolls a different one
// each refresh (stored in sessionStorage). All four targets pick it up:
// CSS vars for HUD/lobby chrome, render.ts + render-gl.ts for the
// canvas, lobby-bg.ts for the lobby particle hues.
const palette = pickRandomPalette();
applyCssPalette(palette);
applyRenderPalette(palette.host, palette.guest, palette.puck, palette.bg);
applyRenderPaletteGL(palette.host, palette.guest, palette.puck, palette.bg);
setLobbyBgPalette(palette.host, palette.guest, palette.puck);
const paletteNameEl = document.getElementById('palette-name');
if (paletteNameEl) paletteNameEl.textContent = palette.name;

showLobbyChoice(els);

// Lobby ambient background — runs while the lobby is on-screen.
const lobbyCanvas = document.getElementById('lobby-canvas') as HTMLCanvasElement | null;
if (lobbyCanvas) startLobbyBg(lobbyCanvas);

// Wire lobby controls.
els.btnHost.addEventListener('click', () => void startHost());
els.btnBot.addEventListener('click', () => {
  const aggro = Number.parseFloat(els.botDifficulty.value);
  startBot(Number.isFinite(aggro) ? aggro : 0.55, getSelectedModifierIds());
});

/** Read the lobby's Mode dropdown into a modifier id array. "classic" is
 *  the baseline and contributes nothing, so we drop it from the wire
 *  payload (empty array == classic on both sides). */
function getSelectedModifierIds(): string[] {
  const id = els.mode.value;
  if (!id || id === 'classic') return [];
  return [id];
}
els.btnJoin.addEventListener('click', () => {
  const code = extractCode(els.joinCode.value);
  if (code) {
    els.joinCode.value = code;
    void startGuest(code);
  } else {
    els.joinCode.focus();
  }
});
els.joinCode.addEventListener('keydown', (e) => {
  if (e.key === 'Enter') els.btnJoin.click();
});
// Trim pasted URLs (e.g. http://.../#join=ABCDEF) down to just the code.
els.joinCode.addEventListener('input', () => {
  const code = extractCode(els.joinCode.value);
  if (code && code !== els.joinCode.value) {
    els.joinCode.value = code;
  }
});

/** Pull a 6-char code out of free-form input (raw code, URL, link with hash). */
function extractCode(raw: string): string | null {
  const upper = raw.trim().toUpperCase();
  // Strip everything that isn't a code character, then take the last 6.
  // This handles raw "ABC123", "  abc123  ", "http://host/#join=ABC123",
  // "https://hockeypuck.cc/?x=1#join=ABC123", etc.
  const cleaned = upper.replace(/[^A-Z0-9]/g, '');
  if (cleaned.length < 6) return null;
  // Use the last 6 chars since the code is at the end of any sensible URL form.
  const code = cleaned.slice(-6);
  return /^[A-Z0-9]{6}$/.test(code) ? code : null;
}
els.btnCancelHost.addEventListener('click', () => location.reload());
els.btnCancelJoin.addEventListener('click', () => location.reload());

// AudioContext requires a user gesture before it'll play anything.
// Init on any first click anywhere on the page; subsequent calls are no-ops.
window.addEventListener('pointerdown', initAudio, { once: true });
window.addEventListener('keydown', initAudio, { once: true });

// Click handler on the mute pill (added once on load — the element is
// always in the DOM, just hidden until the game starts).
const mutePillEl = document.getElementById('mute-pill');
if (mutePillEl) {
  mutePillEl.style.cursor = 'pointer';
  mutePillEl.style.pointerEvents = 'auto';
  mutePillEl.addEventListener('click', () => {
    initAudio();
    setMuted(!isMuted());
    updateMutePill();
  });
}

// ── Settings panel + preferences ──────────────────────────────────────
// The active match registers callbacks for restart / mode change; bot
// mode wires both, host wires both (with multiplayer broadcast), guest
// leaves them null (changes are host-authoritative).
let restartHandler: (() => void) | null = null;
let modeChangeHandler: ((ids: string[]) => void) | null = null;

interface Prefs {
  showStats: boolean;
  showFps: boolean;
  volume: number;  // 0..1
}
function defaultPrefs(): Prefs { return { showStats: true, showFps: true, volume: 0.4 }; }
function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem('hp-prefs');
    if (raw) return { ...defaultPrefs(), ...JSON.parse(raw) as Partial<Prefs> };
  } catch { /* ignore */ }
  return defaultPrefs();
}
function savePrefs(p: Prefs): void {
  try { localStorage.setItem('hp-prefs', JSON.stringify(p)); } catch { /* ignore */ }
}
function applyPrefs(p: Prefs): void {
  document.getElementById('game-stats')?.classList.toggle('hidden', !p.showStats);
  document.getElementById('fps-pill')?.classList.toggle('hidden', !p.showFps);
  document.getElementById('net-pill')?.classList.toggle('hidden', !p.showFps);
  setVolume(p.volume);
}
const prefs: Prefs = loadPrefs();
applyPrefs(prefs);

// Populate the mode dropdown from the MODIFIERS registry.
const settingsModeSelect = document.getElementById('settings-mode') as HTMLSelectElement | null;
if (settingsModeSelect) {
  for (const m of MODIFIERS) {
    const opt = document.createElement('option');
    opt.value = m.id;
    opt.textContent = m.name;
    settingsModeSelect.appendChild(opt);
  }
}

const settingsPanelEl = document.getElementById('settings-panel');
const settingsBackdropEl = document.getElementById('settings-backdrop');
function isSettingsOpen(): boolean {
  return !!settingsPanelEl && !settingsPanelEl.classList.contains('hidden');
}
function openSettings(): void {
  // Only relevant when a match is on-screen.
  if (els.game.classList.contains('hidden')) return;
  // Sync UI to current state.
  if (settingsModeSelect) {
    settingsModeSelect.value = els.mode.value;
  }
  const showStats = document.getElementById('settings-show-stats') as HTMLInputElement | null;
  const showFps = document.getElementById('settings-show-fps') as HTMLInputElement | null;
  const volumeRange = document.getElementById('settings-volume') as HTMLInputElement | null;
  if (showStats) showStats.checked = prefs.showStats;
  if (showFps) showFps.checked = prefs.showFps;
  if (volumeRange) volumeRange.value = String(Math.round(getVolume() * 100));
  settingsPanelEl?.classList.remove('hidden');
  settingsBackdropEl?.classList.remove('hidden');
  // Opening the menu pauses the match (host/bot only — guest's handler
  // is null since pause is host-authoritative).
  if (setPausedHandler) setPausedHandler(true);
}
function closeSettings(): void {
  settingsPanelEl?.classList.add('hidden');
  settingsBackdropEl?.classList.add('hidden');
  if (setPausedHandler) setPausedHandler(false);
}
document.getElementById('settings-pill')?.addEventListener('click', openSettings);
document.getElementById('settings-close')?.addEventListener('click', closeSettings);
settingsBackdropEl?.addEventListener('click', closeSettings);

document.getElementById('settings-show-stats')?.addEventListener('change', (e) => {
  prefs.showStats = (e.target as HTMLInputElement).checked;
  applyPrefs(prefs); savePrefs(prefs);
});
document.getElementById('settings-show-fps')?.addEventListener('change', (e) => {
  prefs.showFps = (e.target as HTMLInputElement).checked;
  applyPrefs(prefs); savePrefs(prefs);
});
document.getElementById('settings-volume')?.addEventListener('input', (e) => {
  const v = Number((e.target as HTMLInputElement).value) / 100;
  prefs.volume = v;
  // Init audio on first interaction so the change is audible immediately.
  initAudio();
  setVolume(v);
  savePrefs(prefs);
});

document.getElementById('settings-restart')?.addEventListener('click', () => {
  if (restartHandler) {
    restartHandler();
    closeSettings();
  }
});

settingsModeSelect?.addEventListener('change', (e) => {
  const id = (e.target as HTMLSelectElement).value;
  els.mode.value = id; // keep lobby dropdown in sync for future matches
  if (modeChangeHandler) {
    modeChangeHandler(id === 'classic' ? [] : [id]);
  }
});

// The active sim (bot or host) registers a setPaused handler that flips
// state.paused and broadcasts to guest if applicable. Guest leaves it
// null — pause is host-authoritative. P/Esc now toggles the settings
// panel (which acts as the pause menu) instead of pausing directly.
let setPausedHandler: ((paused: boolean) => void) | null = null;
window.addEventListener('keydown', (e) => {
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
  if (e.key === 'p' || e.key === 'P' || e.key === 'Escape') {
    e.preventDefault();
    if (isSettingsOpen()) closeSettings();
    else openSettings();
  } else if (e.key === 'm' || e.key === 'M') {
    initAudio();
    setMuted(!isMuted());
    updateMutePill();
  }
});

function updateMutePill(): void {
  const el = document.getElementById('mute-pill');
  if (el) el.textContent = isMuted() ? '🔇 muted (m)' : '🔊 sound (m)';
}

// Auto-join via #join=CODE in URL.
const hashCode = readJoinCodeFromHash();
if (hashCode) {
  els.joinCode.value = hashCode;
  void startGuest(hashCode);
}

async function startHost(): Promise<void> {
  let code: string;
  try {
    code = await createRoom();
  } catch (err) {
    els.hostingStatus.textContent = `Error: ${(err as Error).message}`;
    return;
  }
  // Snapshot the mode selection at room creation — used in connectAndPlay.
  const modifierIds = getSelectedModifierIds();
  showHosting(els, code);
  const link = buildShareLink(code);
  els.btnCopy.onclick = async () => {
    try {
      await navigator.clipboard.writeText(link);
      els.btnCopy.textContent = 'Copied!';
      setTimeout(() => (els.btnCopy.textContent = 'Copy link'), 1200);
    } catch {
      els.btnCopy.textContent = link;
    }
  };
  await connectAndPlay(code, 'host', modifierIds);
}

async function startGuest(code: string): Promise<void> {
  showJoining(els);
  els.joiningStatus.textContent = `Connecting to ${code}…`;
  // Guest's modifiers arrive via the host's `init` message — pass nothing.
  await connectAndPlay(code, 'guest', []);
}

async function connectAndPlay(code: string, role: Role, modifierIds: string[]): Promise<void> {
  let signal: SignalClient | null = null;
  const peer: Peer = await createPeer(role, {
    onChannelOpen: (dc) => {
      // We have a direct peer connection. Close signaling.
      signal?.close();
      startGame(role, dc, modifierIds);
    },
    onChannelMessage: () => { /* handled inside startGame */ },
    onChannelClose: () => {
      setNet('disconnected');
    },
    onConnectionFailed: () => {
      const target = role === 'host' ? els.hostingStatus : els.joiningStatus;
      target.textContent = 'Connection failed (NAT may be too strict). Refresh to retry.';
    },
  });

  signal = connectSignal(code, role, {
    onOpen: () => {
      console.log(`[main:${role}] signal WS open, sending hello`);
      peer.attach(signal!);
      signal!.send({ type: 'hello' });
    },
    onMessage: (msg) => {
      console.log(`[main:${role}] signal msg:`, msg.type);
      if (msg.type === 'peer-absent') {
        const target = role === 'host' ? els.hostingStatus : els.joiningStatus;
        target.textContent = 'Waiting for opponent…';
        return;
      }
      if (msg.type === 'peer-ready') {
        // Both peers attached. Host starts negotiation; guest just waits.
        void peer.negotiate();
        return;
      }
      if (msg.type === 'peer-left') {
        const target = role === 'host' ? els.hostingStatus : els.joiningStatus;
        target.textContent = 'Opponent disconnected.';
        return;
      }
      void peer.handleSignal(msg);
    },
    onError: () => {
      const target = role === 'host' ? els.hostingStatus : els.joiningStatus;
      target.textContent = 'Signaling error.';
    },
    onClose: () => { /* expected after WebRTC connects */ },
  });
}

function setNet(text: string): void {
  els.netPill.textContent = text;
}

/**
 * Local single-tab game vs a bot. No signaling, no WebRTC, no second
 * peer. Useful for fast iteration: just click the button and play.
 * Player is host, bot is guest.
 */
/**
 * Wire the canvas to its rendering surface: keep the drawing buffer
 * sized to the element via ResizeObserver (more reliable than the
 * window 'resize' event — fires for DevTools open, container reflows,
 * etc.) and prebuild the static cache each time so the first paint
 * isn't a stall.
 */
function attachCanvasResize(canvas: HTMLCanvasElement, role: Role): void {
  fitCanvas(canvas);
  warmStaticCache(canvas, role);
  const ro = new ResizeObserver(() => {
    fitCanvas(canvas);
    warmStaticCache(canvas, role);
  });
  ro.observe(canvas);
}

function startBot(aggro: number, modifierIds: string[]): void {
  stopLobbyBg();
  showGame(els, 'host', /* vsBot */ true);
  const modeTag = modifierIds.length > 0 ? ` · ${modifierIds.join('+')}` : '';
  setNet(`local · aggro ${aggro.toFixed(2)}${modeTag}${usingWebGL ? '' : ' · canvas'}`);

  const canvas = els.canvas;
  attachCanvasResize(canvas, 'host');

  const input: InputState = { x: RINK.width / 2, y: RINK.height / 2, seq: 0 };
  attachInput(canvas, input, 'host');
  const perf = createPerfCounter(els.fpsPill);
  const hud: Hud = createHud(els.stage, 'host', /* vsBot */ true);
  const stats: Stats = createStats('host');

  const config = defaultPhysicsConfig();
  applyModifiers(config, modifierIds);
  const state: GameState = newGame(config);
  setPausedHandler = (p) => { state.paused = p; };
  restartHandler = () => {
    resetMatch(state);
    stats.reset();
  };
  modeChangeHandler = (ids) => {
    // Rebuild config from defaults + new modifiers, then reset match.
    const fresh = defaultPhysicsConfig();
    applyModifiers(fresh, ids);
    Object.assign(state.config, fresh);
    resetMatch(state);
    stats.reset();
    const tag = ids.length > 0 ? ` · ${ids.join('+')}` : '';
    setNet(`local · aggro ${aggro.toFixed(2)}${tag}${usingWebGL ? '' : ' · canvas'}`);
  };
  const TICK_HZ = 60;
  const dtTarget = 1000 / TICK_HZ;
  const tickEvents: PhysicsEvent[] = [];

  let raf = 0;
  let acc = 0;
  let prev = performance.now();
  const loop = (now: number) => {
    raf = requestAnimationFrame(loop);
    perf.tick(now);
    const dt = now - prev;
    prev = now;
    // Drop accumulated time after a long pause (tab backgrounded, debugger
    // breakpoint). Without this, the loop burns through hundreds of ticks
    // at 4-per-frame on return, replaying the missed window in fast-forward.
    acc = dt > dtTarget * 5 ? 0 : acc + dt;
    let ticks = 0;
    while (acc >= dtTarget && ticks < 4) {
      setHostTarget(state, input.x, input.y);
      const botTarget = tickBot(state, 'guest', aggro);
      setGuestTarget(state, botTarget.x, botTarget.y);
      tickEvents.length = 0;
      step(state, tickEvents);
      if (tickEvents.length > 0) {
        applyEvents(tickEvents, now);
        stats.applyEvents(tickEvents);
        hud.applyEvents(tickEvents);
        audioApplyEvents(tickEvents);
      }
      acc -= dtTarget;
      ticks++;
    }
    hud.tick(state, now);
    stats.tick(state, now);
    applyHoverTransform(canvas, now);
    render(canvas, state, 'host', now);
  };
  raf = requestAnimationFrame(loop);
}

function startGame(role: Role, dc: RTCDataChannel, modifierIds: string[]): void {
  stopLobbyBg();
  showGame(els, role);
  const modeTag = modifierIds.length > 0 ? ` · ${modifierIds.join('+')}` : '';
  setNet(`connected${modeTag}`);

  const canvas = els.canvas;
  attachCanvasResize(canvas, role);

  const input: InputState = { x: RINK.width / 2, y: RINK.height / 2, seq: 0 };
  attachInput(canvas, input, role);

  const perf = createPerfCounter(els.fpsPill);
  const hud: Hud = createHud(els.stage, role);
  const stats: Stats = createStats(role);

  if (role === 'host') {
    runHost(canvas, dc, input, perf, hud, stats, modifierIds);
  } else {
    runGuest(canvas, dc, input, perf, hud, stats);
  }
}

function runHost(
  canvas: HTMLCanvasElement,
  dc: RTCDataChannel,
  input: InputState,
  perf: { tick(now: number): void },
  hud: Hud,
  stats: Stats,
  modifierIds: string[],
): void {
  const config = defaultPhysicsConfig();
  applyModifiers(config, modifierIds);
  const state: GameState = newGame(config);
  setPausedHandler = (p) => {
    if (state.paused === p) return;
    state.paused = p;
    // Pause halts simulation → s.tick stops advancing → the normal
    // tick-based broadcast trigger never fires. Send immediately so the
    // guest sees the new pause state right away.
    if (dc.readyState === 'open') {
      dc.send(serialize(encodeState(state)));
    }
  };
  restartHandler = () => {
    resetMatch(state);
    stats.reset();
    if (dc.readyState === 'open') {
      dc.send(serialize(encodeState(state)));
    }
  };
  modeChangeHandler = (ids) => {
    const fresh = defaultPhysicsConfig();
    applyModifiers(fresh, ids);
    Object.assign(state.config, fresh);
    resetMatch(state);
    stats.reset();
    // Tell the guest about the new modifier list so they can apply the
    // same config locally for prediction / display.
    if (dc.readyState === 'open') {
      dc.send(serialize({ t: 'init', modifierIds: ids }));
      dc.send(serialize(encodeState(state)));
    }
    const tag = ids.length > 0 ? ` · ${ids.join('+')}` : '';
    setNet(`connected${tag}`);
  };
  let lastBroadcast = 0;
  const TICK_HZ = 60;
  const BROADCAST_EVERY = 2; // ~30 Hz state updates

  // Tell the guest about the active modifiers BEFORE any state messages,
  // so they reconstruct the same PhysicsConfig before simulating any local
  // prediction. One-shot; sent right after the datachannel opens.
  if (dc.readyState === 'open') {
    dc.send(serialize({ t: 'init', modifierIds }));
  }

  // Guest input → set guest paddle target.
  dc.addEventListener('message', (ev) => {
    const msg = parse(ev.data);
    if (msg && msg.t === 'input') {
      setGuestTarget(state, msg.x, msg.y);
    }
  });

  let raf = 0;
  let acc = 0;
  let prev = performance.now();
  const dtTarget = 1000 / TICK_HZ;
  // Events captured during this physics tick, applied locally for
  // animations and shipped to the guest in the next state broadcast.
  const tickEvents: PhysicsEvent[] = [];
  // Events accumulating until next broadcast.
  const pendingBroadcastEvents: PhysicsEvent[] = [];

  const loop = (now: number) => {
    raf = requestAnimationFrame(loop);
    perf.tick(now);
    const dt = now - prev;
    prev = now;
    // Drop accumulated time after a long pause (tab backgrounded). See
    // startBot for the rationale — same fix applies on the host loop.
    acc = dt > dtTarget * 5 ? 0 : acc + dt;
    let ticks = 0;
    while (acc >= dtTarget && ticks < 4) {
      setHostTarget(state, input.x, input.y);
      tickEvents.length = 0;
      step(state, tickEvents);
      if (tickEvents.length > 0) {
        applyEvents(tickEvents, now);
        stats.applyEvents(tickEvents);
        hud.applyEvents(tickEvents);
        audioApplyEvents(tickEvents);
        for (const ev of tickEvents) pendingBroadcastEvents.push(ev);
      }
      acc -= dtTarget;
      ticks++;
      if (state.tick - lastBroadcast >= BROADCAST_EVERY) {
        lastBroadcast = state.tick;
        if (dc.readyState === 'open') {
          const stateEvents = pendingBroadcastEvents.length > 0
            ? pendingBroadcastEvents.slice()
            : undefined;
          pendingBroadcastEvents.length = 0;
          dc.send(serialize(encodeState(state, stateEvents)));
        }
      }
    }
    hud.tick(state, now);
    stats.tick(state, now);
    applyHoverTransform(canvas, now);
    render(canvas, state, 'host', now);
  };
  raf = requestAnimationFrame(loop);

  dc.addEventListener('close', () => {
    cancelAnimationFrame(raf);
    hud.destroy();
  });
}

function runGuest(
  canvas: HTMLCanvasElement,
  dc: RTCDataChannel,
  input: InputState,
  perf: { tick(now: number): void },
  hud: Hud,
  stats: Stats,
): void {
  // Render-only state; updated from host snapshots. Guest still uses the
  // physics struct shape for rendering convenience.
  const state: GameState = newGame();
  let lastInputSent = 0;
  const INPUT_HZ = 60;
  const INPUT_INTERVAL = 1000 / INPUT_HZ;

  dc.addEventListener('message', (ev) => {
    const msg = parse(ev.data);
    if (!msg) return;
    if (msg.t === 'init') {
      // Reconstruct the host's PhysicsConfig from the modifier id list.
      // Default config already in state; just apply on top.
      applyModifiers(state.config, msg.modifierIds);
      const tag = msg.modifierIds.length > 0 ? ` · ${msg.modifierIds.join('+')}` : '';
      setNet(`connected${tag}`);
      return;
    }
    if (msg.t !== 'state') return;
    state.paused = msg.paused === true;
    state.tick = msg.tick;
    state.puck.pos.x = msg.puck.x;
    state.puck.pos.y = msg.puck.y;
    state.puck.vel.x = msg.puck.vx;
    state.puck.vel.y = msg.puck.vy;
    state.host.pos.x = msg.hostPaddle.x;
    state.host.pos.y = msg.hostPaddle.y;
    state.guest.pos.x = msg.guestPaddle.x;
    state.guest.pos.y = msg.guestPaddle.y;
    state.score.host = msg.score.host;
    state.score.guest = msg.score.guest;
    state.phase = msg.phase;
    if (msg.events && msg.events.length > 0) {
      applyEvents(msg.events, performance.now());
      stats.applyEvents(msg.events);
      hud.applyEvents(msg.events);
      audioApplyEvents(msg.events);
    }
  });

  let raf = 0;
  const loop = (now: number) => {
    raf = requestAnimationFrame(loop);
    perf.tick(now);
    if (now - lastInputSent >= INPUT_INTERVAL) {
      lastInputSent = now;
      if (dc.readyState === 'open') {
        dc.send(serialize(encodeInput(input.x, input.y, input.seq)));
      }
    }
    hud.tick(state, now);
    stats.tick(state, now);
    applyHoverTransform(canvas, now);
    render(canvas, state, 'guest', now);
  };
  raf = requestAnimationFrame(loop);

  dc.addEventListener('close', () => {
    cancelAnimationFrame(raf);
    hud.destroy();
  });
}
